import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  getAllTeaCoffees,
  getSingleTeaCoffee,
  createTeaCoffee,
  updateTeaCoffee,
  deleteTeaCoffee,
} from "@/api/new-api/staff-management/menu-manage/tea-coffee";
import { websiteOrderingAvailableItemsToggle } from "@/api/new-api/staff-management/menu-manage/drink";

// Thunks
export const fetchAllTeaCoffees = createAsyncThunk(
  "teaCoffee/fetchAll",
  async (restaurant_id, { rejectWithValue }) => {
    const res = await getAllTeaCoffees(restaurant_id);
    if (res.status === "success") return res.data.tea_coffees;
    return rejectWithValue(res.message);
  }
);

export const fetchSingleTeaCoffee = createAsyncThunk(
  "teaCoffee/fetchOne",
  async (id, { rejectWithValue }) => {
    const res = await getSingleTeaCoffee(id);
    if (res.status === "success") return res.data.tea_coffee;
    return rejectWithValue(res.message);
  }
);

export const createNewTeaCoffee = createAsyncThunk(
  "teaCoffee/create",
  async (data, { rejectWithValue }) => {
    const res = await createTeaCoffee(data);
    if (res.status === "success") return res.data.tea_coffee;
    return rejectWithValue(res.message);
  }
);

export const updateExistingTeaCoffee = createAsyncThunk(
  "teaCoffee/update",
  async ({ id, data }, { rejectWithValue }) => {
    const res = await updateTeaCoffee(id, data);
    if (res.status === "success") return res.data.tea_coffee;
    return rejectWithValue(res.message);
  }
);

export const deleteTeaCoffeeById = createAsyncThunk(
  "teaCoffee/delete",
  async (id, { rejectWithValue }) => {
    const res = await deleteTeaCoffee(id);
    if (res.status === "success") return id;
    return rejectWithValue(res.message);
  }
);

export const updateTeaCoffeeToggle = createAsyncThunk("teaCoffeeToggle/update", async ({ id, type, toggleAvailabilityType }, { rejectWithValue }) => {
    const res = await websiteOrderingAvailableItemsToggle(id, type, toggleAvailabilityType);
    if (res.status === "success") return res.data;
    return rejectWithValue(res.message);
});

// Initial State
const initialState = {
  teaCoffees: [],
  singleTeaCoffee: null,

  getLoading: false,
  createLoading: false,
  updateLoading: false,
  deleteLoading: false,

  success: null,
  error: null,
  requestStatus: false,
};

// Slice
const teaCoffeeSlice = createSlice({
  name: "teaCoffee",
  initialState,
  reducers: {
    clearTeaCoffeeMessages: (state) => {
      state.success = null;
      state.error = null;
      state.requestStatus = false;
    },
  },
  extraReducers: (builder) => {
    builder

      // Fetch all
      .addCase(fetchAllTeaCoffees.pending, (state) => {
        state.getLoading = true;
        state.error = null;
        state.requestStatus = false;
      })
      .addCase(fetchAllTeaCoffees.fulfilled, (state, action) => {
        state.getLoading = false;
        state.teaCoffees = action.payload;
        state.requestStatus = true;
      })
      .addCase(fetchAllTeaCoffees.rejected, (state, action) => {
        state.getLoading = false;
        state.error = action.payload;
        state.requestStatus = false;
      })

      // Fetch single
      .addCase(fetchSingleTeaCoffee.pending, (state) => {
        state.getLoading = true;
        state.error = null;
      })
      .addCase(fetchSingleTeaCoffee.fulfilled, (state, action) => {
        state.getLoading = false;
        state.singleTeaCoffee = action.payload;
        state.requestStatus = true;
      })
      .addCase(fetchSingleTeaCoffee.rejected, (state, action) => {
        state.getLoading = false;
        state.error = action.payload;
        state.requestStatus = false;
      })

      // Create
      .addCase(createNewTeaCoffee.pending, (state) => {
        state.createLoading = true;
        state.error = null;
      })
      .addCase(createNewTeaCoffee.fulfilled, (state, action) => {
        state.createLoading = false;
        state.teaCoffees.push(action.payload);
        state.success = "Tea/Coffee item created successfully!";
        state.requestStatus = true;
      })
      .addCase(createNewTeaCoffee.rejected, (state, action) => {
        state.createLoading = false;
        state.error = action.payload;
        state.requestStatus = false;
      })

      // Update
      .addCase(updateExistingTeaCoffee.pending, (state) => {
        state.updateLoading = true;
        state.error = null;
      })
      .addCase(updateExistingTeaCoffee.fulfilled, (state, action) => {
        state.updateLoading = false;
        state.teaCoffees = state.teaCoffees.map((item) =>
          item.id === action.payload.id ? action.payload : item
        );
        state.success = "Tea/Coffee item updated successfully!";
        state.requestStatus = true;
      })
      .addCase(updateExistingTeaCoffee.rejected, (state, action) => {
        state.updateLoading = false;
        state.error = action.payload;
        state.requestStatus = false;
      })

      // Delete
      .addCase(deleteTeaCoffeeById.pending, (state) => {
        state.deleteLoading = true;
        state.error = null;
      })
      .addCase(deleteTeaCoffeeById.fulfilled, (state, action) => {
        state.deleteLoading = false;
        state.teaCoffees = state.teaCoffees.filter(
          (item) => item.id !== action.payload
        );
        state.success = "Tea/Coffee item deleted successfully!";
        state.requestStatus = true;
      })
      .addCase(deleteTeaCoffeeById.rejected, (state, action) => {
        state.deleteLoading = false;
        state.error = action.payload;
        state.requestStatus = false;
      })

      // toggle website/QRcode availability
      .addCase(updateTeaCoffeeToggle.pending, (state) => {
          state.updateLoading = true;
          state.error = null;
          state.requestStatus = false;
      })
      .addCase(updateTeaCoffeeToggle.fulfilled, (state, action) => {
        state.updateLoading = false;

        const { id, toggleAvailabilityType } = action.meta.arg;

        state.teaCoffees = state.teaCoffees.map((drink) => {
          if (drink.id !== id) return drink;

          if (toggleAvailabilityType === "qr_code") {
            return {
              ...drink,
              is_qr_code_order_available: !drink.is_qr_code_order_available,
            };
          }

          return {
            ...drink,
            is_website_order_available: !drink.is_website_order_available,
          };
        });

        state.success = "Updated successfully!";
        state.requestStatus = true;
      })
      .addCase(updateTeaCoffeeToggle.rejected, (state, action) => {
          state.updateLoading = false;
          state.error = action.payload;
          state.requestStatus = false;
      })
  },
});

export const { clearTeaCoffeeMessages } = teaCoffeeSlice.actions;
export default teaCoffeeSlice.reducer;
